export interface Session {
  readonly token: string;
  readonly tokenType: string;
  readonly expiresAt: Date;
  readonly accountId: number;
  readonly legacyId: number | null;
  readonly username: string;
}

export interface Credentials {
  readonly username: string;
  readonly password: string;
}

export interface Registration extends Credentials {
  readonly email: string;
  readonly referrer?: string | undefined;
}

export class AuthError extends Error {
  override readonly name = 'AuthError';

  constructor(
    message: string,
    readonly kind: AuthErrorKind,
    readonly status: number | null = null,
    readonly code: string | null = null,
    readonly fieldErrors: Readonly<Record<string, readonly string[]>> = {},
  ) {
    super(message);
  }
}

export type AuthErrorKind =
  | 'invalid-credentials'
  | 'invalid-request'
  | 'conflict'
  | 'rate-limited'
  | 'session-invalid'
  | 'unreachable'
  | 'server-error';

export interface AuthClient {
  login(credentials: Credentials): Promise<Session>;
  register(registration: Registration): Promise<Session>;
  logout(session: Session): Promise<void>;
  currentSession(session: Session): Promise<Session>;
}

/** 30s skew allowance for clock drift; the API's session lifetime is 30 days, so losing half a minute of it costs nothing. */
export function isSessionUsable(session: Session, now: Date = new Date()): boolean {
  return session.expiresAt.getTime() - now.getTime() > 30_000;
}

export function authorizationHeader(session: Session): string {
  return `${session.tokenType} ${session.token}`;
}
